import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, Loader2, LogIn, Sparkles, ArrowLeft, KeyRound, CheckCircle, RefreshCw, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { loginStudent, verifyStudentCode, setStudentMpin, resetStudentMpin, getStudentSignupSource } from '../services/conceptPlus1Service';
import type { StudentSession } from '../types';
import { TornCard } from '@branon-shared/ui/TornCard';
import { IconBadge } from '@branon-shared/ui/IconBadge';
import { AmbientDoodles } from '@branon-shared/ui/Doodle';

const inputClass =
  'w-full px-4 py-3.5 border-2 border-gray-200 rounded-card focus:outline-none focus:border-coral text-center text-lg font-mono tracking-wider transition-colors disabled:opacity-60';

const primaryBtnClass =
  'w-full py-3.5 rounded-btn font-quicksand font-bold text-base border-2 border-ink shadow-sticker hover:translate-y-0.5 hover:shadow-none disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-sticker disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2';

type LoginMode = 'login' | 'setup' | 'reset';
// Reset flow is two steps: confirm which kind of account this is (by
// student code) before showing the Parent Linking Code form -- B2C
// accounts (signup_source='b2c_direct') never have a linking code their
// parent has ever seen, so they get a different screen instead.
type ResetStep = 'code' | 'form' | 'b2c-message';

export default function StudentLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [mode, setMode] = useState<LoginMode>('login');
  const [resetStep, setResetStep] = useState<ResetStep>('code');
  const [studentCode, setStudentCode] = useState('');
  const [mpin, setMpin] = useState('');
  const [confirmMpin, setConfirmMpin] = useState('');
  const [parentCode, setParentCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLogin = async () => {
    setError('');
    setIsLoading(true);

    try {
      const data = await loginStudent(studentCode.trim().toUpperCase(), mpin);

      if (data.success && data.student_id && data.student_code) {
        // Check if grade is 11 or 12
        if (data.grade_level && (data.grade_level < 11 || data.grade_level > 12)) {
          setError('This app is for Class 11 & 12 students only');
          setIsLoading(false);
          return;
        }

        const session: StudentSession = {
          student_id: data.student_id,
          student_code: data.student_code,
          expires_at: data.expires_at || data.expiry_date || '',
          school_id: data.school_id || 0,
          grade_level: data.grade_level || 11,
          section_id: data.section_id,
          section_name: data.section_name,
          planType: data.plan_type || 'core',
          board: data.board,
          state_board: data.state_board,
        };
        login(session);
        navigate('/dashboard');
      } else {
        // Check if error is due to missing MPIN
        if (data.error?.toLowerCase().includes('mpin')) {
          const verifyResult = await verifyStudentCode(studentCode.trim().toUpperCase());
          if (verifyResult.success && verifyResult.requires_setup) {
            setMode('setup');
            setError('');
            setIsLoading(false);
            return;
          }
        }
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupMpin = async () => {
    setError('');
    setSuccess('');

    // Validate inputs
    if (!studentCode.trim()) {
      setError('Please enter your student code');
      return;
    }
    if (!parentCode.trim()) {
      setError('Please enter your parent linking code');
      return;
    }
    if (mpin.length !== 6 || !/^\d+$/.test(mpin)) {
      setError('MPIN must be exactly 6 digits');
      return;
    }
    if (mpin !== confirmMpin) {
      setError('MPINs do not match');
      return;
    }

    setIsLoading(true);

    try {
      const result = await setStudentMpin(studentCode.trim().toUpperCase(), parentCode.trim(), mpin);

      if (result.success) {
        setSuccess(result.message || 'MPIN set successfully! You can now log in.');
        setMode('login');
        setParentCode('');
        setConfirmMpin('');
      } else {
        setError(result.error || 'Failed to set MPIN');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
      console.error('Setup error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetMpin = async () => {
    setError('');
    setSuccess('');

    // Validate inputs
    if (!studentCode.trim()) {
      setError('Please enter your student code');
      return;
    }
    if (!parentCode.trim()) {
      setError('Please enter your parent linking code');
      return;
    }
    if (mpin.length < 4 || mpin.length > 6 || !/^\d+$/.test(mpin)) {
      setError('MPIN must be 4-6 digits');
      return;
    }
    if (mpin !== confirmMpin) {
      setError('MPINs do not match');
      return;
    }

    setIsLoading(true);

    try {
      const result = await resetStudentMpin(studentCode.trim().toUpperCase(), parentCode.trim(), mpin);

      if (result.success) {
        setSuccess(result.message || 'MPIN reset successfully! You can now log in.');
        setMode('login');
        setParentCode('');
        setMpin('');
        setConfirmMpin('');
      } else {
        setError(result.error || 'Failed to reset MPIN');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
      console.error('Reset error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetCodeContinue = async () => {
    setError('');
    setSuccess('');

    if (!studentCode.trim()) {
      setError('Please enter your student code');
      return;
    }

    setIsLoading(true);

    try {
      const result = await getStudentSignupSource(studentCode.trim().toUpperCase());

      if (result.success && result.signup_source === 'b2c_direct') {
        setResetStep('b2c-message');
      } else {
        // school_license, or lookup failed -- fall back to the existing
        // Parent Linking Code form, the flow that's always existed.
        setResetStep('form');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
      console.error('Signup source lookup error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      if (mode === 'login' && studentCode.trim() && mpin.trim()) {
        handleLogin();
      } else if (mode === 'setup' && studentCode.trim() && parentCode.trim() && mpin.trim() && confirmMpin.trim()) {
        handleSetupMpin();
      } else if (mode === 'reset' && resetStep === 'code' && studentCode.trim()) {
        handleResetCodeContinue();
      } else if (mode === 'reset' && resetStep === 'form' && studentCode.trim() && parentCode.trim() && mpin.trim() && confirmMpin.trim()) {
        handleResetMpin();
      }
    }
  };

  const resetForm = () => {
    setError('');
    setSuccess('');
    setMpin('');
    setConfirmMpin('');
    setParentCode('');
    setResetStep('code');
  };

  return (
    <div className="min-h-[100dvh] bg-night flex items-start sm:items-center justify-center px-4 py-8 sm:py-24 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <AmbientDoodles />
      </div>

      <div className="max-w-lg w-full relative z-10">
        <Link
          to="/welcome"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 font-inter text-sm transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Welcome
        </Link>

        <TornCard
          stage={
            <div className="text-center pb-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 text-marigold text-xs font-bold uppercase tracking-widest mb-4">
                <Sparkles className="w-3 h-3" />
                {mode === 'login' ? 'Student Login' : mode === 'setup' ? 'Setup MPIN' : 'Reset MPIN'}
              </div>
              <h1 className="font-quicksand text-3xl sm:text-4xl font-bold text-white mb-2 text-balance">
                {mode === 'login' ? 'Welcome Back!' : mode === 'setup' ? 'Set Your MPIN' : 'Reset MPIN'}
              </h1>
              <p className="text-white/70 font-inter">
                {mode === 'login'
                  ? 'Log in to continue your learning journey'
                  : mode === 'setup'
                    ? 'Create a 6-digit MPIN to secure your account'
                    : resetStep === 'code'
                      ? 'Enter your student code to get started'
                      : resetStep === 'b2c-message'
                        ? "Here's how to get back in"
                        : 'Enter your details to get a new MPIN'}
              </p>
            </div>
          }
        >
          <div className="space-y-5">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <IconBadge icon={mode === 'login' ? LogIn : KeyRound} tone="coral" size={40} />
                <h2 className="font-quicksand text-xl font-bold text-ink">
                  {mode === 'login' ? 'Enter Your Code' : mode === 'setup' ? 'Setup Your MPIN' : 'Reset Your MPIN'}
                </h2>
              </div>
              <p className="text-sm text-gray-600 font-inter">
                {mode === 'login'
                  ? 'Enter the student code you received during registration'
                  : mode === 'reset' && resetStep === 'code'
                    ? "We'll check how your account was set up"
                    : mode === 'reset' && resetStep === 'b2c-message'
                      ? ''
                      : 'You\'ll need your Parent Linking Code (from your registration card)'}
              </p>
            </div>

            {/* Student Code - visible for login/setup, and reset's first step */}
            {!(mode === 'reset' && resetStep === 'b2c-message') && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Student Code
                </label>
                <input
                  type="text"
                  value={studentCode}
                  onChange={(e) => setStudentCode(e.target.value.toUpperCase())}
                  onKeyPress={handleKeyPress}
                  placeholder="STUXXXXXX"
                  className={inputClass}
                  disabled={isLoading || (mode === 'reset' && resetStep === 'form')}
                  autoFocus
                />
              </div>
            )}

            {/* Parent Code - For setup mode, and reset's form step (B2B only) */}
            {(mode === 'setup' || (mode === 'reset' && resetStep === 'form')) && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Parent Linking Code
                </label>
                <input
                  type="text"
                  value={parentCode}
                  onChange={(e) => setParentCode(e.target.value.toUpperCase())}
                  onKeyPress={handleKeyPress}
                  placeholder="e.g. PAREF955D"
                  className={inputClass}
                  disabled={isLoading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Find this on your registration card or ask your parent
                </p>
              </div>
            )}

            {/* MPIN - For login, setup, and reset's form step */}
            {(mode === 'login' || mode === 'setup' || (mode === 'reset' && resetStep === 'form')) && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {mode === 'setup' ? 'Create MPIN' : mode === 'reset' ? 'New MPIN' : 'MPIN'}
                </label>
                <input
                  type="password"
                  value={mpin}
                  onChange={(e) => setMpin(e.target.value.replace(/\D/g, ''))}
                  onKeyPress={handleKeyPress}
                  placeholder={mode === 'setup' ? 'Create 6-digit MPIN' : 'Enter your MPIN'}
                  className={inputClass}
                  disabled={isLoading}
                  maxLength={6}
                />
              </div>
            )}

            {/* Confirm MPIN - For setup mode, and reset's form step */}
            {(mode === 'setup' || (mode === 'reset' && resetStep === 'form')) && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm MPIN
                </label>
                <input
                  type="password"
                  value={confirmMpin}
                  onChange={(e) => setConfirmMpin(e.target.value.replace(/\D/g, ''))}
                  onKeyPress={handleKeyPress}
                  placeholder="Re-enter MPIN"
                  className={inputClass}
                  disabled={isLoading}
                  maxLength={6}
                />
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="flex items-start gap-3 p-4 bg-success-soft border-2 border-success rounded-card">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <p className="text-sm text-success font-medium">{success}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-3 p-4 bg-critical-soft border-2 border-critical rounded-card">
                <AlertCircle className="w-5 h-5 text-critical flex-shrink-0 mt-0.5" />
                <p className="text-sm text-critical font-medium">{error}</p>
              </div>
            )}

            {/* Action Button */}
            {mode === 'login' && (
              <button
                onClick={handleLogin}
                disabled={!studentCode.trim() || !mpin.trim() || isLoading}
                className={`${primaryBtnClass} bg-coral text-white`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Log In
                  </>
                )}
              </button>
            )}

            {mode === 'setup' && (
              <button
                onClick={handleSetupMpin}
                disabled={!studentCode.trim() || !parentCode.trim() || !mpin.trim() || !confirmMpin.trim() || isLoading}
                className={`${primaryBtnClass} bg-marigold text-ink`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-5 h-5" />
                    Set MPIN
                  </>
                )}
              </button>
            )}

            {mode === 'reset' && resetStep === 'code' && (
              <button
                onClick={handleResetCodeContinue}
                disabled={!studentCode.trim() || isLoading}
                className={`${primaryBtnClass} bg-night text-white`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Checking...
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            )}

            {mode === 'reset' && resetStep === 'form' && (
              <button
                onClick={handleResetMpin}
                disabled={!studentCode.trim() || !parentCode.trim() || !mpin.trim() || !confirmMpin.trim() || isLoading}
                className={`${primaryBtnClass} bg-night text-white`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    Reset MPIN
                  </>
                )}
              </button>
            )}

            {mode === 'reset' && resetStep === 'b2c-message' && (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <IconBadge icon={Users} tone="night" size={52} />
                </div>
                <p className="text-gray-700 font-inter">
                  Your Branon account doesn't use a Parent Linking Code. Ask your parent to open the{' '}
                  <a
                    href="https://app.branonedu.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-coral font-bold hover:underline"
                  >
                    Branon Parent app
                  </a>
                  , log in with their email and password, and reset your MPIN from your profile page.
                </p>
                <button
                  onClick={() => { setMode('login'); resetForm(); }}
                  className="text-ink font-bold hover:underline text-sm"
                >
                  Back to Login
                </button>
              </div>
            )}

            {/* Mode Switch Links */}
            <div className="text-center pt-4 border-t-2 border-gray-100 space-y-2">
              {mode === 'login' && (
                <>
                  <p className="text-sm text-gray-600">
                    First time logging in?{' '}
                    <button
                      onClick={() => { setMode('setup'); resetForm(); }}
                      className="text-ink font-bold hover:underline"
                    >
                      Setup MPIN
                    </button>
                  </p>
                  <p className="text-sm text-gray-600">
                    Forgot your MPIN?{' '}
                    <button
                      onClick={() => { setMode('reset'); resetForm(); }}
                      className="text-coral font-bold hover:underline"
                    >
                      Reset MPIN
                    </button>
                  </p>
                </>
              )}
              {(mode === 'setup' || mode === 'reset') && (
                <p className="text-sm text-gray-600">
                  Already have MPIN?{' '}
                  <button
                    onClick={() => { setMode('login'); resetForm(); }}
                    className="text-ink font-bold hover:underline"
                  >
                    Go to Login
                  </button>
                </p>
              )}
            </div>
          </div>
        </TornCard>

        <p className="text-center text-sm text-white/50 mt-6 font-inter">
          Having trouble? Contact your school administrator
        </p>
      </div>
    </div>
  );
}
